/**
 * Microphone capture for realtime voice sessions.
 *
 * Captures at 16 kHz (the backend's `voice_audio` input format) through a
 * DEDICATED AudioContext — the shared playback context must not be forced to
 * 16 kHz; browsers resample the mic stream into whatever rate the capture
 * context runs at. An `AudioWorklet` (`public/worklets/pcm-capture-worklet.js`)
 * accumulates 640-sample (40 ms) Int16 frames — ~1.7 KB as base64, far under
 * the 32 KB socket frame cap — and this module base64-encodes them for the
 * `voice_audio` wire frame.
 *
 * `getUserMedia` errors propagate to the caller: `NotAllowedError` (permission
 * denied) and `NotFoundError` (no mic hardware) get dedicated copy in the
 * voice session store.
 */

const CAPTURE_SAMPLE_RATE = 16_000;
const WORKLET_URL = '/worklets/pcm-capture-worklet.js';

export interface MicCaptureOptions {
  /** One 40 ms capture frame: base64 PCM16 mono @ 16 kHz + its RMS level
   *  (0..1, for the session bar's mic pulse). */
  onFrame: (chunkB64: string, rms: number) => void;
}

export interface MicCapture {
  /** Release the mic (tracks + context). Idempotent. */
  stop: () => void;
}

function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

export async function startMicCapture(opts: MicCaptureOptions): Promise<MicCapture> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.mediaDevices?.getUserMedia ||
    typeof AudioContext === 'undefined' ||
    typeof AudioWorkletNode === 'undefined'
  ) {
    throw new Error('Audio capture is not supported in this browser');
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });

  let ctx: AudioContext;
  try {
    ctx = new AudioContext({ sampleRate: CAPTURE_SAMPLE_RATE });
    await ctx.audioWorklet.addModule(WORKLET_URL);
  } catch (err) {
    stream.getTracks().forEach((t) => t.stop());
    throw err;
  }
  // Created inside the mic-button click handler, so this resolves immediately;
  // best-effort for anything unusual.
  if (ctx.state === 'suspended') void ctx.resume().catch(() => {});

  const source = ctx.createMediaStreamSource(stream);
  const node = new AudioWorkletNode(ctx, 'pcm-capture');
  node.port.onmessage = (e: MessageEvent<{ pcm: ArrayBuffer; rms: number }>) => {
    opts.onFrame(toBase64(e.data.pcm), e.data.rms);
  };
  source.connect(node);
  // The worklet emits silence; connecting to the destination keeps the graph
  // pulled so `process()` runs.
  node.connect(ctx.destination);

  let stopped = false;
  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      node.port.onmessage = null;
      try {
        source.disconnect();
        node.disconnect();
      } catch {
        /* already disconnected */
      }
      stream.getTracks().forEach((t) => t.stop());
      void ctx.close().catch(() => {});
    },
  };
}
